import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Check, X, FileDown, Share2, Copy, Loader2, Moon, Sun } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PlaybookDokument from "./PlaybookDokument";
import "./playbook.css";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

/* ── Del-modal: én stabil offentlig lenke med passord ── */
function DelModal({ onLukk }) {
  const [lenke, setLenke] = useState(null);
  const [laster, setLaster] = useState(true);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    api.post("/admin/os/playbook/share")
      .then((r) => setLenke(r.data.link))
      .catch(feil)
      .finally(() => setLaster(false));
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  const url = lenke ? `${window.location.origin}/playbook/s/${lenke.token}` : "";
  const kopier = (tekst, hva) => {
    navigator.clipboard?.writeText(tekst).then(
      () => toast.success(`${hva} kopiert`),
      () => toast.error("Kunne ikke kopiere")
    );
  };
  const toggleAktiv = async () => {
    try {
      const r = await api.patch("/admin/os/playbook/share", { active: !lenke.active });
      setLenke(r.data.link);
      toast.success(r.data.link.active ? "Lenken er aktivert" : "Lenken er deaktivert");
    } catch (e) { feil(e); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[14vh] px-4" data-testid="pb-del-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <div className="sak-modal relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl border border-[#E7E4DB] p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-[15px] font-semibold">Ekstern lenke</h2>
          <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="pb-del-lukk">
            <X size={16} />
          </button>
        </div>
        <p className="text-[12px] text-neutral-500 mb-5">Offentlig, passordbeskyttet lesevisning av hele playbooken. Del lenken og passordet hver for seg.</p>
        {laster ? (
          <p className="text-[12.5px] text-neutral-400">Henter lenke …</p>
        ) : lenke ? (
          <div className="grid gap-4">
            <div>
              <p className="pb-mono text-[9px] uppercase tracking-[0.22em] text-neutral-400 mb-1.5">Lenke</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-[12px] text-neutral-800 border border-neutral-200 rounded-lg px-3 py-2.5" data-testid="pb-del-url">{url}</code>
                <button type="button" className="adm-btn-ghost adm-btn-sm shrink-0" onClick={() => kopier(url, "Lenken")} data-testid="pb-del-kopier-url"><Copy size={12} /></button>
              </div>
            </div>
            <div>
              <p className="pb-mono text-[9px] uppercase tracking-[0.22em] text-neutral-400 mb-1.5">Passord</p>
              <div className="flex items-center gap-2">
                <code className="pb-mono text-[16px] tracking-[0.3em] font-semibold text-neutral-900 border border-neutral-200 rounded-lg px-4 py-2" data-testid="pb-del-kode">{lenke.passcode}</code>
                <button type="button" className="adm-btn-ghost adm-btn-sm shrink-0" onClick={() => kopier(lenke.passcode, "Passordet")} data-testid="pb-del-kopier-kode"><Copy size={12} /></button>
                <span className={`ml-auto pb-mono text-[9.5px] uppercase tracking-[0.14em] ${lenke.active ? "text-emerald-600" : "text-red-500"}`}>
                  {lenke.active ? "Aktiv" : "Deaktivert"}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
              <p className="text-[11px] text-neutral-400">Lenken er stabil — deaktiver for å stenge tilgang umiddelbart.</p>
              <button type="button" className={lenke.active ? "adm-btn-danger" : "adm-btn"} onClick={toggleAktiv} data-testid="pb-del-toggle">
                {lenke.active ? "Deaktiver" : "Aktiver"}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-[12.5px] text-red-500">Kunne ikke hente lenken.</p>
        )}
      </div>
    </div>
  );
}

export default function PlaybookAdmin() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [lagreStatus, setLagreStatus] = useState(null);
  const [pdfLaster, setPdfLaster] = useState(false);
  const [pdfMeny, setPdfMeny] = useState(false);
  const [delModal, setDelModal] = useState(false);
  const hashGjort = useRef(false);

  const last = useCallback(async () => {
    try {
      const r = await api.get("/admin/os/playbook");
      setData(r.data);
    } catch (e) { feil(e); }
  }, []);
  useEffect(() => { last(); }, [last]);

  const patchSection = async (sid, body) => {
    try {
      setLagreStatus("lagrer");
      const r = await api.patch(`/admin/os/playbook/sections/${sid}`, body);
      setData((d) => ({
        ...d,
        sections: d.sections.map((s) => (s.id === sid ? r.data.section : s)),
        lastUpdated: r.data.section.updatedAt,
      }));
      setLagreStatus(`Lagret ${new Date().toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}`);
      if (body.data && "limit" in (body.data || {})) last();
    } catch (e) { setLagreStatus(null); feil(e); }
  };

  const patchStrategi = async (cardId, status) => {
    try {
      setLagreStatus("lagrer");
      const r = await api.patch(`/admin/os/strategy/${cardId}`, { status });
      setData((d) => ({
        ...d,
        strategy: d.strategy.map((c) => (c.id === cardId ? { ...c, status: r.data.card.status } : c)),
      }));
      setLagreStatus(`Lagret ${new Date().toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}`);
    } catch (e) { setLagreStatus(null); feil(e); }
  };

  const flytt = async (s, retning) => {
    const sortert = [...data.sections].sort((a, b) => a.order - b.order);
    const i = sortert.findIndex((x) => x.id === s.id);
    const j = i + retning;
    if (j < 0 || j >= sortert.length) return;
    await patchSection(sortert[i].id, { order: sortert[j].order });
    await patchSection(sortert[j].id, { order: sortert[i].order });
  };

  const kopierLenke = (key) => {
    const url = `${window.location.origin}${window.location.pathname}#${key}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Lenke til seksjonen kopiert"),
      () => toast.error("Kunne ikke kopiere lenken")
    );
  };

  // Deep-link: scroll til #seksjon ved innlasting
  useEffect(() => {
    if (!data || hashGjort.current) return;
    hashGjort.current = true;
    const key = window.location.hash.replace("#", "");
    if (key) setTimeout(() => document.getElementById(`pb-anker-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
  }, [data]);

  // Print-oppsett (fallback: nettleser-print skal også være pen)
  useEffect(() => {
    document.body.classList.add("pb-utskrift");
    const stil = document.createElement("style");
    stil.id = "pb-page-stil";
    stil.textContent = "@page { size: A4 portrait; margin: 0; }";
    document.head.appendChild(stil);
    const foer = () => {
      document.querySelectorAll(".adm-canvas.adm-light").forEach((el) => {
        el.classList.add("pb-var-lys");
        el.classList.remove("adm-light");
        el.classList.add("adm-dark");
      });
    };
    const etter = () => {
      document.querySelectorAll(".adm-canvas.pb-var-lys").forEach((el) => {
        el.classList.remove("pb-var-lys", "adm-dark");
        el.classList.add("adm-light");
      });
    };
    window.addEventListener("beforeprint", foer);
    window.addEventListener("afterprint", etter);
    return () => {
      document.body.classList.remove("pb-utskrift");
      document.getElementById("pb-page-stil")?.remove();
      window.removeEventListener("beforeprint", foer);
      window.removeEventListener("afterprint", etter);
    };
  }, []);

  // PDF — server-generert, lastes ned direkte
  const lastNedPdf = async (tema) => {
    if (pdfLaster) return;
    setPdfLaster(true);
    try {
      const r = await api.get(`/admin/os/playbook/pdf?theme=${tema}`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = tema === "light" ? "Norwegian-AI-Summit-2027-Playbook-Light.pdf" : "Norwegian-AI-Summit-2027-Playbook.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF lastet ned");
    } catch (e) {
      feil(e);
    } finally {
      setPdfLaster(false);
    }
  };

  if (!data) {
    return (
      <div className="flex gap-12" data-testid="pb-laster">
        <div className="pb-toc hidden xl:block">
          <div className="pb-skjelett h-3 w-20 mb-4" />
          {[...Array(9)].map((_, i) => <div key={i} className="pb-skjelett h-2.5 w-full mb-2.5" style={{ opacity: 1 - i * 0.09 }} />)}
        </div>
        <div className="pb-doc flex-1 min-w-0">
          <div className="pb-skjelett h-[300px] w-full" />
          <div className="pb-skjelett h-5 w-48 mt-12" />
          <div className="pb-skjelett h-24 w-full mt-5" />
          <div className="pb-skjelett h-5 w-56 mt-12" />
          <div className="grid sm:grid-cols-3 gap-3.5 mt-5">
            {[...Array(3)].map((_, i) => <div key={i} className="pb-skjelett h-28" />)}
          </div>
        </div>
      </div>
    );
  }

  const verktoy = (
    <div className="flex items-center gap-2 shrink-0 flex-wrap">
      <button type="button" onClick={() => setDelModal(true)} className="pb-cta" title="Del ekstern lenke med passord" data-testid="pb-del-btn">
        <Share2 size={12} /> Del
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => setPdfMeny((v) => !v)}
          disabled={pdfLaster}
          className="pb-cta pb-pdf-btn"
          title="Last ned som PDF"
          data-testid="pb-pdf-btn"
        >
          {pdfLaster ? <><Loader2 size={12} className="animate-spin" /> Genererer …</> : <><FileDown size={12} /> PDF</>}
        </button>
        {pdfMeny && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setPdfMeny(false)} />
            <div className="absolute right-0 top-full mt-2 z-40 w-[200px] bg-white border border-[#E7E4DB] rounded-xl shadow-xl p-1.5" data-testid="pb-pdf-meny">
              <button
                type="button"
                onClick={() => { setPdfMeny(false); lastNedPdf("dark"); }}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12.5px] font-medium text-left hover:bg-neutral-50 transition-colors"
                data-testid="pb-pdf-dark"
              >
                <Moon size={13} className="opacity-60" /> PDF — mørk
              </button>
              <button
                type="button"
                onClick={() => { setPdfMeny(false); lastNedPdf("light"); }}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12.5px] font-medium text-left hover:bg-neutral-50 transition-colors"
                data-testid="pb-pdf-light"
              >
                <Sun size={13} className="opacity-60" /> PDF — lys
              </button>
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => setEdit((x) => !x)}
        className={edit ? "pb-cta fylt" : "pb-cta"}
        data-testid="pb-edit-btn"
      >
        {edit ? <><Check size={12} /> Ferdig</> : <><Pencil size={12} /> Edit</>}
      </button>
    </div>
  );

  return (
    <div data-testid="playbook-root">
      <PlaybookDokument
        data={data}
        edit={edit}
        interaktiv
        verktoy={verktoy}
        onPatchSection={patchSection}
        onPatchStrategi={patchStrategi}
        onFlytt={flytt}
        onKopier={kopierLenke}
      />

      {edit && (
        <div className="pb-editbar" data-testid="pb-editbar">
          <span className="pb-livedot" />
          <span className="pb-mono text-[9.5px] uppercase tracking-[0.14em] text-neutral-500">
            {lagreStatus === "lagrer" ? "Lagrer …" : lagreStatus || "Redigerer — lagres automatisk"}
          </span>
          <button type="button" onClick={() => setEdit(false)} className="adm-btn inline-flex items-center gap-1.5" data-testid="pb-editbar-ferdig">
            <Check size={13} /> Ferdig
          </button>
        </div>
      )}

      {delModal && <DelModal onLukk={() => setDelModal(false)} />}
    </div>
  );
}
